import { useCallback, useMemo, useState } from "react";

import { useFormikContext } from "formik";

import useDidUpdateEffect from "@hooks/useDidUpdateEffect";
import { useRouter } from "@lib/router";
import debounce from "@utils/debounce";

import loadZipCode from "../services/loadZipCode";
import { TZipCodeUpdateHookArgs, TZipCodeUpdateHookReturns } from "../types";

type TSuggestedData = {
  data?: string;
  error?: string | undefined
};

const useZipCodeUpdate = ({
  countryFieldKey,
  cityFieldKey,
  houseNoFieldKey,
  streetFieldKey,
  zipCodeFieldKey,
  country: countryValue,
  city: cityValue,
  street: streetValue,
  houseNo: houseNoValue,
  isAddressSelectedFromAddressBook,
  isUpdateFromZipCode
}: TZipCodeUpdateHookArgs): TZipCodeUpdateHookReturns => {
  const { setFieldValue, values, errors, touched } = useFormikContext<any>();
  const { defaultLocale } = useRouter();
  const isZipError = !!(errors[zipCodeFieldKey] && touched[zipCodeFieldKey]);
  const [isLoading, setIsLoading] = useState(false);

  const street = streetValue ?? values[streetFieldKey!];
  const houseNumber = houseNoValue ?? values[houseNoFieldKey!];
  const city = cityValue ?? values[cityFieldKey!];
  const country = countryValue ?? values[countryFieldKey!];

  const loadZip = useCallback(
    async (addressData: Record<"street" | "houseNumber" | "city" | "country", string | undefined>) => {
      setIsLoading(true);
      let suggestedData = { data: "", error: "" } as TSuggestedData;
      if (!isAddressSelectedFromAddressBook && isUpdateFromZipCode) {
        suggestedData = await loadZipCode({
          street: addressData.street,
          houseNumber: addressData.houseNumber,
          city: addressData.city,
          country: addressData.country,
          defaultLocale: defaultLocale!,
        });
      }

      setIsLoading(false);
      if (suggestedData?.error) {
        console.error(suggestedData.error);
        return;
      }
      if (suggestedData?.data) {
        setFieldValue(zipCodeFieldKey, suggestedData.data);
      }
    },
    [isAddressSelectedFromAddressBook, defaultLocale, setFieldValue, zipCodeFieldKey, isUpdateFromZipCode]
  );

  const debouncedZipLoading = useMemo(() => debounce(loadZip), [loadZip]);

  useDidUpdateEffect(() => {
    if (!street || !city) {
      return;
    }

    debouncedZipLoading({ street, houseNumber, city, country });
  }, [debouncedZipLoading, street, houseNumber, city, country]);

  return { isZipCodeLoading: isLoading, isZipError };
};
export default useZipCodeUpdate;
